import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingCart, User, Search, Menu, X } from 'lucide-react';

const Header18 = ({ cartCount = 0 }) => {
    const navigate = useNavigate();
    const [scrolled, setScrolled] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 50);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        if (mobileMenuOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [mobileMenuOpen]);

    const navLinks = [
        { label: 'Home', path: '/template18' },
        { label: 'Shop', path: '/template18/shop' },
        { label: 'About', path: '/template18/about' },
        { label: 'Contact', path: '/template18/contact' },
        { label: 'Orders', path: '/template18/orders' },
    ];

    const handleNavClick = (path) => {
        navigate(path);
        setMobileMenuOpen(false);
    };

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Vollkorn:wght@400;600;700&family=Work+Sans:wght@300;400;500&display=swap');
            `}</style>

            <header
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    zIndex: 50,
                    backgroundColor: scrolled ? 'rgba(250, 243, 235, 0.97)' : 'transparent',
                    borderBottom: scrolled ? '1px solid #D4C5B0' : '1px solid transparent',
                    transition: 'background-color 0.3s ease, border-color 0.3s ease',
                    backdropFilter: scrolled ? 'blur(8px)' : 'none',
                }}
            >
                <div
                    style={{
                        maxWidth: '1400px',
                        margin: '0 auto',
                        padding: '0 24px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        height: '72px',
                    }}
                >
                    {/* Left: Brand */}
                    <h1
                        onClick={() => navigate('/template18')}
                        style={{
                            fontFamily: "'Vollkorn', Georgia, serif",
                            fontSize: '24px',
                            fontWeight: 700,
                            color: '#7C2D12',
                            cursor: 'pointer',
                            letterSpacing: '2px',
                            margin: 0,
                        }}
                    >
                        PURANI DILLI
                    </h1>

                    {/* Center: Nav Links (Desktop) */}
                    <nav
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '32px',
                        }}
                        className="purani-desktop-nav"
                    >
                        {navLinks.map((link) => (
                            <button
                                key={link.label}
                                onClick={() => handleNavClick(link.path)}
                                style={{
                                    fontFamily: "'Vollkorn', Georgia, serif",
                                    fontSize: '15px',
                                    fontWeight: 400,
                                    color: '#1C1917',
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    padding: 0,
                                    letterSpacing: '1px',
                                    transition: 'color 0.3s ease',
                                }}
                                onMouseEnter={(e) => (e.target.style.color = '#7C2D12')}
                                onMouseLeave={(e) => (e.target.style.color = '#1C1917')}
                            >
                                {link.label}
                            </button>
                        ))}
                    </nav>

                    {/* Right: Icons */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '18px' }}>
                        <Search
                            size={20}
                            style={{ color: '#78716C', cursor: 'pointer', transition: 'color 0.3s ease' }}
                            onMouseEnter={(e) => (e.currentTarget.style.color = '#7C2D12')}
                            onMouseLeave={(e) => (e.currentTarget.style.color = '#78716C')}
                        />
                        <User
                            size={20}
                            style={{ color: '#78716C', cursor: 'pointer', transition: 'color 0.3s ease' }}
                            onClick={() => navigate('/template18/profile')}
                            onMouseEnter={(e) => (e.currentTarget.style.color = '#7C2D12')}
                            onMouseLeave={(e) => (e.currentTarget.style.color = '#78716C')}
                        />
                        <div
                            style={{ position: 'relative', cursor: 'pointer' }}
                            onClick={() => navigate('/template18/cart')}
                        >
                            <ShoppingCart
                                size={20}
                                style={{ color: '#78716C', transition: 'color 0.3s ease' }}
                                onMouseEnter={(e) => (e.currentTarget.style.color = '#7C2D12')}
                                onMouseLeave={(e) => (e.currentTarget.style.color = '#78716C')}
                            />
                            {cartCount > 0 && (
                                <span
                                    style={{
                                        position: 'absolute',
                                        top: '-8px',
                                        right: '-8px',
                                        backgroundColor: '#7C2D12',
                                        color: '#FAF3EB',
                                        fontSize: '10px',
                                        fontWeight: 700,
                                        fontFamily: "'Work Sans', sans-serif",
                                        width: '18px',
                                        height: '18px',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    {cartCount}
                                </span>
                            )}
                        </div>

                        {/* Mobile Hamburger */}
                        <button
                            className="purani-mobile-menu-btn"
                            onClick={() => setMobileMenuOpen(true)}
                            style={{
                                display: 'none',
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                padding: 0,
                            }}
                        >
                            <Menu size={24} style={{ color: '#7C2D12' }} />
                        </button>
                    </div>
                </div>
            </header>

            {/* Mobile Full-Screen Overlay */}
            <div
                style={{
                    position: 'fixed',
                    inset: 0,
                    zIndex: 100,
                    backgroundColor: '#FAF3EB',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: mobileMenuOpen ? 1 : 0,
                    pointerEvents: mobileMenuOpen ? 'auto' : 'none',
                    transition: 'opacity 0.3s ease',
                }}
            >
                <button
                    onClick={() => setMobileMenuOpen(false)}
                    style={{
                        position: 'absolute',
                        top: '24px',
                        right: '24px',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: 0,
                    }}
                >
                    <X size={28} style={{ color: '#7C2D12' }} />
                </button>

                <nav style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '28px' }}>
                    {navLinks.map((link) => (
                        <button
                            key={link.label}
                            onClick={() => handleNavClick(link.path)}
                            style={{
                                fontFamily: "'Vollkorn', Georgia, serif",
                                fontSize: '26px',
                                fontWeight: 600,
                                color: '#7C2D12',
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                padding: 0,
                                letterSpacing: '2px',
                                transition: 'color 0.3s ease',
                            }}
                            onMouseEnter={(e) => (e.target.style.color = '#A0845C')}
                            onMouseLeave={(e) => (e.target.style.color = '#7C2D12')}
                        >
                            {link.label}
                        </button>
                    ))}
                </nav>

                {/* Vintage ornament */}
                <div style={{ marginTop: '40px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{ width: '40px', height: '1px', backgroundColor: '#A0845C' }} />
                    <span style={{ color: '#A0845C', fontSize: '18px' }}>&#10043;</span>
                    <div style={{ width: '40px', height: '1px', backgroundColor: '#A0845C' }} />
                </div>
            </div>

            {/* Responsive Styles */}
            <style>{`
                .purani-desktop-nav {
                    display: flex !important;
                }
                .purani-mobile-menu-btn {
                    display: none !important;
                }
                @media (max-width: 768px) {
                    .purani-desktop-nav {
                        display: none !important;
                    }
                    .purani-mobile-menu-btn {
                        display: block !important;
                    }
                }
            `}</style>
        </>
    );
};

export default Header18;
